/**
 * RunAI Chat - Kullanici-Agent Chat Sistemi
 * Kullanici kendi agent'lariyla konusur, konusmalar hafizaya kaydolur.
 */
import { Bot, Code, Database, Globe, Image, Mic, MicOff, Music, Send, Video, Volume2, VolumeX } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { API_URL } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'

interface Agent {
  id: number
  name: string
  agent_name: string
  expertise: string
  interest_area: string
  bio: string
  user_type: string
  llm_type: string
  known_llm: string
  level: number
  bond_level: number
  bond_xp: number
  total_chats: number
  last_seen_at: string | null
  is_demo: boolean
  is_platform: boolean
  can_process_image: boolean
  can_process_audio: boolean
  can_process_video: boolean
  can_generate_code: boolean
  can_search_web: boolean
  can_analyze_data: boolean
}

interface Message {
  id?: number
  role: 'user' | 'assistant'
  content: string
  created_at?: string
}

const LLM_LABELS: Record<string, { label: string; color: string }> = {
  runai_cloud: { label: 'RunAI Cloud', color: '#3b82f6' },
  cloud_api:   { label: 'Cloud API',   color: '#10b981' },
  local_llm:   { label: 'Local LLM',   color: '#f59e0b' },
  petals:      { label: 'Petals P2P',  color: '#8b5cf6' },
  cf_gateway:  { label: 'CF Gateway',  color: '#f6821f' },
  cf_workers:  { label: 'CF Workers',  color: '#f6821f' },
}

// Capability ikonlari
function CapabilityIcons({ agent, size = 10 }: { agent: Agent; size?: number }) {
  const caps = [
    { flag: agent.can_process_image, icon: Image,    label: 'Image' },
    { flag: agent.can_process_audio, icon: Music,    label: 'Audio' },
    { flag: agent.can_process_video, icon: Video,    label: 'Video' },
    { flag: agent.can_generate_code, icon: Code,     label: 'Code' },
    { flag: agent.can_search_web,    icon: Globe,    label: 'Web Search' },
    { flag: agent.can_analyze_data,  icon: Database, label: 'Data' },
  ].filter(c => c.flag)

  if (!caps.length) {return null}
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {caps.map(c => (
        <c.icon key={c.label} size={size} color="var(--text-muted)" />
      ))}
    </div>
  )
}

// Agent listesi satiri
function AgentRow({ agent, selected, onClick }: { agent: Agent; selected: boolean; onClick: () => void }) {
  const llm = LLM_LABELS[agent.llm_type] || LLM_LABELS.runai_cloud
  return (
    <div onClick={onClick} style={{
      padding: '9px 12px', cursor: 'pointer',
      background: selected ? 'var(--primary-color)15' : 'transparent',
      borderLeft: selected ? '3px solid var(--primary-color)' : '3px solid transparent',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          background: `hsl(${(agent.id * 47) % 360}, 55%, 42%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, color: '#fff', fontWeight: 700,
        }}>
          {agent.name.charAt(0)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {agent.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: llm.color + '20', color: llm.color, fontWeight: 600, flexShrink: 0 }}>
              {llm.label}
            </span>
            <span style={{ fontSize: 9, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {agent.interest_area || agent.expertise}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>Lv.{agent.level}</span>
            <span style={{ fontSize: 9, color: '#f59e0b' }}>Bond {agent.bond_level}</span>
            <CapabilityIcons agent={agent} size={9} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default function RunAIChatPage() {
  const { token } = useAuth()
  const [searchParams] = useSearchParams()
  const [agents, setAgents] = useState<Agent[]>([])
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingAgents, setLoadingAgents] = useState(true)
  const [ttsEnabled, setTtsEnabled] = useState(false)
  const [micActive, setMicActive] = useState(false)
  const [search, setSearch] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)
  const recognitionRef = useRef<unknown>(null)

  const authToken = token || ''
  const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }

  useEffect(() => {
    fetch(`${API_URL}/api/chat/agents`, { headers })
      .then(r => r.json())
      .then((d: { agents: Agent[] }) => {
        const list = d.agents || []
        setAgents(list)
        setLoadingAgents(false)
        const agentParam = searchParams.get('agent')
        const msgParam = searchParams.get('msg')
        if (agentParam) {
          const found = list.find(a => a.id === Number(agentParam))
          if (found) { setSelectedAgent(found); if (msgParam) {setInput(msgParam)} }
        }
      })
      .catch(() => setLoadingAgents(false))
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedAgent) {return}
    setMessages([])
    fetch(`${API_URL}/api/chat/history/${selectedAgent.id}?limit=50`, { headers })
      .then(r => r.json())
      .then((d: { messages: Message[] }) => setMessages(d.messages || []))
      .catch(() => {})
  }, [selectedAgent]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const sendMessage = async () => {
    if (!input.trim() || !selectedAgent || loading) {return}
    const userMsg: Message = { role: 'user', content: input }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST', headers,
        body: JSON.stringify({ agent_id: selectedAgent.id, message: input }),
      })
      const data = await res.json() as { response: string }
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }])
      if (ttsEnabled && data.response) {
        const utt = new SpeechSynthesisUtterance(data.response)
        utt.lang = 'en-US'
        window.speechSynthesis.speak(utt)
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }])
    } finally { setLoading(false) }
  }

  const toggleMic = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {return}
    if (micActive) { (recognitionRef.current as { stop: () => void } | null)?.stop(); setMicActive(false); return }
    const SR = (window as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown }).SpeechRecognition
      || (window as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition
    if (!SR) {return}
    const rec = SR as { lang: string; continuous: boolean; interimResults: boolean; onresult: unknown; onerror: unknown; onend: unknown; start: () => void }
    rec.lang = 'en-US'; rec.continuous = false; rec.interimResults = false
    rec.onresult = (e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => { setInput(String((e.results[0] as ArrayLike<{ transcript: string }>)[0].transcript)); setMicActive(false) }
    rec.onerror = () => setMicActive(false)
    rec.onend = () => setMicActive(false)
    recognitionRef.current = rec; rec.start(); setMicActive(true)
  }

  // Kategorilere ayir
  const q = search.toLowerCase()
  const filtered = agents.filter(a =>
    a.name.toLowerCase().includes(q) ||
    (a.interest_area || '').toLowerCase().includes(q) ||
    (a.expertise || '').toLowerCase().includes(q)
  )
  const platformAgents = filtered.filter(a => a.is_platform)
  const myAgents       = filtered.filter(a => !a.is_platform && !a.is_demo)
  const demoAgents     = filtered.filter(a => !a.is_platform && a.is_demo)

  const llmInfo = selectedAgent ? (LLM_LABELS[selectedAgent.llm_type] || LLM_LABELS.runai_cloud) : null

  const SectionHeader = ({ label, count }: { label: string; count: number }) => (
    <div style={{ padding: '8px 12px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between' }}>
      <span>{label}</span>
      <span style={{ fontWeight: 400 }}>{count}</span>
    </div>
  )

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', background: 'var(--body-bg)', overflow: 'hidden' }}>

      {/* Sol panel */}
      <div style={{ width: 270, minWidth: 220, borderRight: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', background: 'var(--card-bg)' }}>
        <div style={{ padding: '14px 12px 8px', borderBottom: '1px solid var(--border-color)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Chat with Agent</div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agents..."
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--body-bg)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box', outline: 'none' }} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingAgents ? (
            <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 12 }}>Loading...</div>
          ) : (
            <>
              {/* Benim Agent'larım */}
              {myAgents.length > 0 && (
                <>
                  <SectionHeader label="My Agents" count={myAgents.length} />
                  {myAgents.map(a => <AgentRow key={a.id} agent={a} selected={selectedAgent?.id === a.id} onClick={() => setSelectedAgent(a)} />)}
                </>
              )}

              {/* Platform Agents */}
              {platformAgents.length > 0 && (
                <>
                  <SectionHeader label="Platform Agents" count={platformAgents.length} />
                  {platformAgents.map(a => <AgentRow key={a.id} agent={a} selected={selectedAgent?.id === a.id} onClick={() => setSelectedAgent(a)} />)}
                </>
              )}

              {/* Demo Agents */}
              {demoAgents.length > 0 && (
                <>
                  <SectionHeader label="Demo Agents" count={demoAgents.length} />
                  {demoAgents.map(a => <AgentRow key={a.id} agent={a} selected={selectedAgent?.id === a.id} onClick={() => setSelectedAgent(a)} />)}
                </>
              )}

              {filtered.length === 0 && (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>No agents found</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Sag panel - Chat */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Header */}
        {selectedAgent ? (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--card-bg)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: `hsl(${(selectedAgent.id * 47) % 360}, 55%, 42%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 700 }}>
              {selectedAgent.name.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{selectedAgent.name}</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {llmInfo && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: llmInfo.color + '20', color: llmInfo.color, fontWeight: 600 }}>
                    {llmInfo.label}
                  </span>
                )}
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selectedAgent.interest_area || selectedAgent.expertise}</span>
                <span style={{ fontSize: 10, color: '#f59e0b' }}>Bond Lv.{selectedAgent.bond_level}</span>
                <CapabilityIcons agent={selectedAgent} size={11} />
              </div>
            </div>
            <button onClick={() => setTtsEnabled(!ttsEnabled)}
              style={{ background: ttsEnabled ? 'var(--primary-color)20' : 'transparent', border: '1px solid var(--border-color)', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: ttsEnabled ? 'var(--primary-color)' : 'var(--text-muted)' }}>
              {ttsEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}
            </button>
          </div>
        ) : (
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Select an agent to start chatting</div>
          </div>
        )}

        {/* Mesajlar */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!selectedAgent ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', marginTop: '20vh' }}>
              <Bot size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 14 }}>Choose an agent from the left panel</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Your conversations are saved automatically</div>
            </div>
          ) : messages.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', marginTop: '15vh' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: `hsl(${(selectedAgent.id * 47) % 360}, 55%, 42%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#fff', fontWeight: 700, marginBottom: 12 }}>
                {selectedAgent.name.charAt(0)}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{selectedAgent.name}</div>
              <div style={{ fontSize: 12, marginTop: 4, maxWidth: 300, textAlign: 'center' }}>{selectedAgent.bio?.slice(0, 100) || selectedAgent.interest_area}</div>
              <div style={{ fontSize: 11, marginTop: 8 }}>Start the conversation below</div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '72%', padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user' ? 'var(--primary-color)' : 'var(--card-bg)',
                  color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                  border: msg.role === 'assistant' ? '1px solid var(--border-color)' : 'none',
                  fontSize: 13, lineHeight: 1.5,
                }}>
                  {msg.content}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '10px 14px', borderRadius: '16px 16px 16px 4px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: 13 }}>
                Thinking...
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button onClick={toggleMic} disabled={!selectedAgent}
            style={{ background: micActive ? '#ef444420' : 'transparent', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 10px', cursor: selectedAgent ? 'pointer' : 'not-allowed', color: micActive ? '#ef4444' : 'var(--text-muted)', flexShrink: 0 }}>
            {micActive ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendMessage() } }}
            placeholder={selectedAgent ? `Message ${selectedAgent.name}...` : 'Select an agent first'}
            disabled={!selectedAgent || loading} rows={1}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--body-bg)', color: 'var(--text-primary)', fontSize: 13, resize: 'none', outline: 'none', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto', fontFamily: 'inherit' }} />
          <button onClick={() => void sendMessage()} disabled={!selectedAgent || !input.trim() || loading}
            style={{ background: 'var(--primary-color)', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: selectedAgent && input.trim() && !loading ? 'pointer' : 'not-allowed', color: '#fff', opacity: selectedAgent && input.trim() && !loading ? 1 : 0.5, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  )
}
